import { css } from "@emotion/css";
import { ArrowUpRightFromSquareIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens, space, text } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React from "react";
import { EmptyStateDefaultIllustration } from "../../Illustrations/EmptyStateDefaultIllustration";
import { NoEntitiesIllustration } from "../../Illustrations/NoEntitiesIllustration";
import { ExternalLink } from "../../Link/ExternalLink";
import type { PrimaryPageAction, PageAction } from "../../PageActions/PageActions";
import { toInternalPageAction, toInternalPrimaryPageAction, PageActionComponent } from "../../PageActions/PageActions";
type ExcludePageActionType<T extends {
    type: unknown;
}, TAction = PageAction> = TAction extends T ? Exclude<TAction, T> : TAction;
export type InlineEmptyStateOnboardingPrimaryPageAction = ExcludePageActionType<{
    type: "custom";
}, PrimaryPageAction>;
export type InlineEmptyStateOnboardingSecondaryPageAction = ExcludePageActionType<{
    type: "custom";
}, PageAction> & {
    buttonType: "secondary";
};
export interface InlineEmptyStateExternalOnboardingPageAction {
    label: string;
    accessibleName?: string;
    disabled?: boolean | Promise<unknown>;
    onClick: (event: React.MouseEvent | undefined) => Promise<unknown> | void;
}
export type LearnMoreActionType = {
    label: string;
    onClick?: () => void;
    path: string;
};
type InlineOnboardingIllustrationType = "NoEntities" | "EmptyState";
interface SharedInlineEmptyStateOnboardingProps {
    illustration?: InlineOnboardingIllustrationType;
    entityName: string;
    learnMore?: LearnMoreActionType;
}
export interface InlineEmptyStateOnboardingWithPrimaryActionProps extends SharedInlineEmptyStateOnboardingProps {
    primaryAction: InlineEmptyStateOnboardingPrimaryPageAction;
    secondaryAction?: InlineEmptyStateOnboardingSecondaryPageAction;
}
export interface InlineEmptyStateOnboardingWithExternalPrimaryActionProps extends SharedInlineEmptyStateOnboardingProps {
    title?: string;
    description: string;
    externalAction: InlineEmptyStateExternalOnboardingPageAction;
}
export type InlineEmptyStateOnboardingProps = InlineEmptyStateOnboardingWithPrimaryActionProps | InlineEmptyStateOnboardingWithExternalPrimaryActionProps;
export function InlineEmptyStateOnboarding(props: InlineEmptyStateOnboardingProps): JSX.Element {
    const { illustration = "NoEntities", entityName, learnMore } = props;
    const description = "description" in props ? props.description : `Start creating by clicking on ${props.primaryAction.label}`;
    const title = "title" in props && props.title ? props.title : `You don't have any ${entityName} yet.`;
    return (<div className={inlineEmptyStateOnboardingStyles.root}>
            <InlineEmptyStateImage illustration={illustration}/>
            <h2 className={inlineEmptyStateOnboardingStyles.heading}>{title}</h2>
            <p className={inlineEmptyStateOnboardingStyles.description}>{description}</p>
            <InlineEmptyStateOnboardingPageActions entityName={entityName} primaryAction={"primaryAction" in props ? props.primaryAction : undefined} externalLinkAction={"externalAction" in props ? props.externalAction : undefined} secondaryAction={"secondaryAction" in props ? props.secondaryAction : undefined} learnMore={learnMore}/>
        </div>);
}
interface InlineEmptyStateImageProps {
    illustration: InlineOnboardingIllustrationType;
}
function InlineEmptyStateImage({ illustration }: InlineEmptyStateImageProps) {
    return <div className={inlineEmptyStateImageStyles}>{getIllustration(illustration)}</div>;
}
function getIllustration(illustration: InlineOnboardingIllustrationType): React.ReactElement {
    switch (illustration) {
        case "EmptyState":
            return <EmptyStateDefaultIllustration />;
        case "NoEntities":
            return <NoEntitiesIllustration />;
        default:
            exhaustiveCheck(illustration, "Not all illustration types have been handled");
    }
}
const inlineEmptyStateImageStyles = css({
    maxWidth: "120px",
    maxHeight: "120px",
});
interface InlineEmptyStateOnboardingPageActionsProps {
    primaryAction?: InlineEmptyStateOnboardingPrimaryPageAction;
    externalLinkAction?: InlineEmptyStateExternalOnboardingPageAction;
    secondaryAction?: InlineEmptyStateOnboardingSecondaryPageAction;
    learnMore?: LearnMoreActionType;
    entityName: string;
}
function InlineEmptyStateOnboardingPageActions({ primaryAction, externalLinkAction, secondaryAction, learnMore }: InlineEmptyStateOnboardingPageActionsProps): JSX.Element {
    return (<div className={inlineEmptyStateOnboardingStyles.actions}>
            {primaryAction && <OnboardingPrimaryPageAction primaryAction={primaryAction}/>}
            {externalLinkAction && <OnboardingExternalPageAction externalAction={externalLinkAction}/>}
            {secondaryAction && <OnboardingPageAction action={secondaryAction}/>}
            {learnMore && <ExternalLink href={learnMore.path} label={learnMore.label} onClick={learnMore.onClick}/>}
        </div>);
}
function OnboardingPrimaryPageAction({ primaryAction }: {
    primaryAction: InlineEmptyStateOnboardingPrimaryPageAction;
}) {
    const internalPrimaryPageAction = toInternalPrimaryPageAction(primaryAction);
    return <PageActionComponent item={internalPrimaryPageAction}/>;
}
function OnboardingPageAction({ action }: {
    action: InlineEmptyStateOnboardingSecondaryPageAction;
}) {
    const internalPageAction = toInternalPageAction(action);
    return <PageActionComponent item={internalPageAction}/>;
}
function OnboardingExternalPageAction({ externalAction }: {
    externalAction: InlineEmptyStateExternalOnboardingPageAction;
}) {
    const internalPageAction = toInternalPageAction({ type: "button", buttonType: "secondary", icon: <ArrowUpRightFromSquareIcon size={16}/>, ...externalAction });
    return <PageActionComponent item={internalPageAction}/>;
}
const inlineEmptyStateOnboardingStyles = {
    root: css({
        padding: space[48],
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    }),
    heading: css({
        color: themeTokens.color.text.primary,
        font: text.heading.small,
        margin: 0,
        marginTop: space[16],
    }),
    actions: css({
        marginTop: space[24],
        display: "flex",
        gap: space[16],
        alignItems: "center",
        "@media (max-width: 1088px)": {
            justifyContent: "center",
            flexWrap: "wrap",
        },
    }),
    description: css({
        color: themeTokens.color.text.secondary,
        font: text.body.regular.medium,
        margin: 0,
        marginTop: space[8],
    }),
};
